import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import styled, { keyframes } from 'styled-components';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Skeleton from '@material-ui/lab/Skeleton';
import { withStyles } from '@material-ui/core';
import { getColor } from 'common/constants/_colors';
import { SIZES } from 'common/constants/sizes';
import { Flex } from './layout-components';
import { Inline } from './Text';
import { TABLE_SKELETON_NUMBER_OF_ROWS } from 'common/constants/constants';
import { getRandomIntInclusive } from 'common/utils/utils';

export const loadingAnimationKeyFrames = keyframes`
 0%, 100% { opacity: 0; }
 50% { opacity: 1; }
`;

const defaultCell = {
  paddingLeft: '2rem',
  paddingRight: '2rem',
  border: '0',
  borderBottom: `1px solid ${getColor('catskillWhite')}`,
  whiteSpace: 'nowrap'
};

const CirclesSkeletonStyle = styled(Flex)`
  & > span:not(:first-child) {
    margin-left: -5px;
  }
`;

const CircleSkeletonStyled = withStyles({
  root: {
    backgroundColor: getColor('catskillWhite'),
    width: '3rem',
    height: '3rem'
  }
})(Skeleton);

const defaultBorderedCircleStyle = {
  backgroundColor: 'transparent',
  border: `10px solid ${getColor('catskillWhite')}`,
  width: '3rem',
  height: '3rem'
};

const BorderedCircleSkeletonStyled = withStyles({
  root: {
    ...defaultBorderedCircleStyle
  }
})(Skeleton);

const BorderedCircleSkeletonNoAnimationStyled = withStyles({
  root: {
    ...defaultBorderedCircleStyle,
    animation: false
  }
})(Skeleton);

const defaultLineStyle = {
  backgroundColor: getColor('catskillWhite'),
  borderRadius: '0.5rem'
};

export const LineSkeletonStyled = withStyles({
  root: {
    ...defaultLineStyle
  }
})(Skeleton);

const LineSkeletonNoAnimationStyled = withStyles({
  root: {
    ...defaultLineStyle,
    animation: false
  }
})(Skeleton);

const SkeletonCell = withStyles({
  root: {
    ...defaultCell
  }
})(TableCell);

const SkeletonHeadCell = withStyles({
  root: {
    color: getColor('gray'),
    fontSize: SIZES.small,
    padding: '2rem 2rem 1rem 2rem',
    border: '0px',
    letterSpacing: '0.01071em'
  }
})(TableCell);

const SkeletonBlankHeabCell = withStyles({
  root: {
    ...defaultCell,
    height: '4rem'
  }
})(TableCell);

const getCircleSkelatons = (isRandomNum, circlesCount) => {
  const circles = [];
  for (let i = 1; i <= circlesCount; i++) {
    circles.push(<CircleSkeleton animation='wave' variant='circle' key={i} />);
  }
  return circles;
};

const getSkeleton = ({ skeletonColumn }) => {
  switch (skeletonColumn) {
    case 'avatar':
      return <CircleSkeleton />;
    case 'avatars':
      return <CirclesSkeleton />;
    case 'avatars-random':
      return <CirclesSkeleton isRandomNum />;
    case 'circle-random':
      return <CircleSkeleton isRandomSize />;
    case 'singleline':
      return <LineSkeleton animation='wave' />;
    case 'singleline-random':
      return <LineSkeleton animation='wave' isRandomSize />;
    case 'multiline':
      return <MultilineSkeleton animation='wave' isRandomSize />;
    case 'avatar-singleline':
      return <CircleSinglelineSkeleton animation='wave' isRandomSize />;
    case 'avatar-multiline':
      return <CircleMultilineSkeleton animation='wave' isRandomSize />;
    default:
      return null;
  }
};

const TableSkeleton = ({
  showSkeletonColumnsHead,
  columns = [],
  skeletonColumns = [],
  rowsNumber = TABLE_SKELETON_NUMBER_OF_ROWS
}) => {
  return (
    <Table>
      {showSkeletonColumnsHead && (
        <TableHead>
          <TableRow>
            {columns.map((column, idx) => {
              return (
                <SkeletonHeadCell key={idx}>
                  {_.get(column, 'name', '')}
                </SkeletonHeadCell>
              );
            })}
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        <TableRow>
          {!showSkeletonColumnsHead && (
            <SkeletonBlankHeabCell colSpan={parseInt(skeletonColumns.length)} />
          )}
        </TableRow>
        {_.range(rowsNumber).map(row_idx => (
          <TableRow key={row_idx}>
            {skeletonColumns.map((skeletonColumn, cell_idx) => (
              <SkeletonCell
                key={`${cell_idx}${row_idx}`}
                width={`${100 / skeletonColumns.length}%`}
              >
                {getSkeleton({ skeletonColumn })}
              </SkeletonCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const CircleSkeleton = ({ isRandomSize, size }) => {
  const [randomCircleSize] = useState(`${getRandomIntInclusive(1, 3)}rem`);

  const circleSize = isRandomSize
    ? { width: randomCircleSize, height: randomCircleSize }
    : size
    ? { width: `${size}rem`, height: `${size}rem` }
    : {};

  return (
    <CircleSkeletonStyled animation='wave' variant='circle' {...circleSize} />
  );
};

export const BorderedCircleSkeleton = ({
  size,
  borderSize = 10,
  withoutAnimation = false
}) => {
  const circleSize = {
    width: `${size}rem`,
    height: `${size}rem`
  };
  const border = `${borderSize}px solid ${getColor('catskillWhite')}`;
  if (withoutAnimation) {
    return (
      <BorderedCircleSkeletonNoAnimationStyled
        variant='circle'
        {...circleSize}
        style={{ border }}
      />
    );
  }
  return (
    <BorderedCircleSkeletonStyled
      variant='circle'
      {...circleSize}
      style={{ border }}
    />
  );
};

export const CirclesSkeleton = ({ isRandomNum, numOfCircles }) => {
  const [circlesCount] = useState(
    isRandomNum ? getRandomIntInclusive(1, 3) : numOfCircles ? numOfCircles : 3
  );

  return (
    <CirclesSkeletonStyle>
      {getCircleSkelatons(isRandomNum, circlesCount)}
    </CirclesSkeletonStyle>
  );
};

export const LineSkeleton = ({
  isRandomSize,
  size = 0,
  withoutAnimation = false
}) => {
  const [randomLineSize] = useState(`${getRandomIntInclusive(50, 100)}%`);

  const lineSize = isRandomSize
    ? { width: randomLineSize }
    : size
    ? { width: `${size}%` }
    : {};

  if (withoutAnimation) {
    return <LineSkeletonNoAnimationStyled {...lineSize} />;
  }
  return <LineSkeletonStyled {...lineSize} />;
};

export const MultilineSkeleton = ({ isRandomSize, size }) => {
  const [randomLineSize] = useState(
    isRandomSize
      ? { width: `${getRandomIntInclusive(50, 100)}%` }
      : size
      ? { width: `${size}%` }
      : {}
  );
  const [secondRandomLineSize] = useState(
    isRandomSize
      ? { width: `${getRandomIntInclusive(50, 100)}%` }
      : size
      ? { width: `${size}%` }
      : {}
  );

  return (
    <>
      <LineSkeletonStyled {...randomLineSize} />
      <LineSkeletonStyled {...secondRandomLineSize} />
    </>
  );
};

export const CircleSinglelineSkeleton = ({ isRandomSize, size }) => {
  return (
    <Flex items='center' minWidth='7rem'>
      <Inline>
        <CircleSkeleton />
      </Inline>
      <Inline width='100%' pl='0.5rem'>
        <LineSkeleton isRandomSize={isRandomSize} size={size} />
      </Inline>
    </Flex>
  );
};

export const CircleMultilineSkeleton = ({ isRandomSize, size }) => {
  return (
    <Flex items='center' minWidth='7rem'>
      <Inline>
        <CircleSkeleton />
      </Inline>
      <Inline width='100%' pl='0.5rem'>
        <MultilineSkeleton isRandomSize={isRandomSize} size={size} />
      </Inline>
    </Flex>
  );
};

TableSkeleton.propTypes = {
  showSkeletonColumnsHead: PropTypes.bool,
  columns: PropTypes.array,
  skeletonColumns: PropTypes.array
};

CircleSkeleton.propTypes = {
  isRandomSize: PropTypes.bool,
  size: PropTypes.number
};

CirclesSkeleton.propTypes = {
  isRandomNum: PropTypes.bool,
  numOfCircles: PropTypes.number
};

LineSkeleton.propTypes = {
  isRandomSize: PropTypes.bool,
  size: PropTypes.number
};

MultilineSkeleton.propTypes = {
  isRandomSize: PropTypes.bool,
  size: PropTypes.number
};

CircleSinglelineSkeleton.propTypes = {
  isRandomSize: PropTypes.bool,
  size: PropTypes.number
};

CircleMultilineSkeleton.propTypes = {
  isRandomSize: PropTypes.bool,
  size: PropTypes.number
};

export default TableSkeleton;
